import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { Address, PaymentInfoItem, PaymentMethod, PaymentGateway } from "types";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { RemovalStatusBadge } from "components/Removalstatus";
import { StatusBadge } from "components/Productlocationstatus";
import { Addresses } from "types";
import PaymentMethods from "components/PaymentMethods";
import { displayPaymentPaidByName } from "components/orderSummary";

const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    // border: "1px solid #dee2e6",          // Light border
};
import { getAuthCookie } from "../../utils/authCookies";
import {
    fetchRemovalPaymentGateway,
    fetchRemovalPaymentMethods,
    resolveRemovalPaymentMethodsMembershipId,
    resolveRemovalSellerOrganizationId,
    resolveRemovalPaymentSellerOrganizationId,
    createRemovalStripePaymentIntent,
} from "api/removalApi";

const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

type Selected = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}


const RealtorViewRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const location = useLocation();
    const isTeammember = location.pathname.includes('/teammember');
    const baseRoute = isTeammember ? '/teammember' : '/realtor';
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [installer, setInstaller] = useState<any[]>([]);
    const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);
    const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
    const [updatedProducts, setUpdatedProducts] = useState<any[]>([]);
    // const [productNotes, setProductNotes] = useState<Record<string, { bin_location: string; note: string }>>({});
    // const [updatedRotationalUnits, setUpdatedRotationalUnits] = useState<Record<string, {
    //     current_status: string;
    //     restock_quantity: number;
    //     writeoff_quantity: number;
    //     reason_to_writeoff: string;
    //     bin_location: string;
    //     note: string;
    // }>>({});
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
    const [payment, setPayment] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
    const [clientSecret, setClientSecret] = useState<string | "">();

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address.address_details ? address.address_details._id : address._id,
                label: address.label,
            }))
            : [];

    const priorityOptions = [
        { value: 1, label: "Critical" },
        { value: 2, label: "High Priority" },
        { value: 3, label: "Normal" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] = useState<Address | null>(null);
    const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] = useState<Address | null>(null);

    const [selectedInstallationAddress, setSelectedInstallationAddress] = useState<Address | null>(null);

    useEffect(() => {
        const fetchRemovalOrder = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    showError(data.error || "Failed to fetch removal order");
                    return;
                }

                setRemovalOrder(data);
                setUpdatedProducts(data.line_items || []);
                console.log("Fetched removal order:", data);
            } catch (err) {
                showError("Something went wrong while fetching removal order.");
                console.error(err);
            }
        };

        if (id) {
            fetchRemovalOrder();
        }
    }, [id]);

    useEffect(() => {
        if (removalOrder?.removal_details?.removal_address?.latitude && removalOrder?.removal_details?.removal_address?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.latitude,
                lng: removalOrder.removal_details.removal_address.longitude,
            });
        }
    }, [removalOrder]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            order_number: removalOrder?.order_number,
            priority: removalOrder?.priority || 0,
            scheduled_date: removalOrder?.removal_details?.scheduled_date.split("T")[0] || "",
            scheduled_jobs: 0,
            restock_warehouse: "",
            installeId: removalOrder?.removal_details?.installer_id,
            billing_address: "",
            removal_address: "",
            isSameAsBilling: false,
            billingAddressId: "",
            removalAddressId: "",
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
            enable_partial_payment: false,
            partial_amount: 0,
            transactionId: "",
            paymentMethod: "",
            installation_charge: removalOrder?.removal_cost,
            discount: removalOrder?.discount_amount || 0,
            subtotal: removalOrder?.subtotal_amount,
            total: removalOrder?.total_amount,
            tax: 0,
        },
        validationSchema: Yup.object({
            // installer: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            // try {
            //     // Example file upload handling
            //     const imageUrl = values.image ? await uploadFile(values.image) : null;

            //     const response = await fetch(
            //         `${process.env.REACT_APP_API_URL}/categories`,
            //         {
            //             method: "POST",
            //             headers: {
            //                 "Content-Type": "application/json",
            //                 Authorization: `Bearer ${token}`,
            //             },
            //             body: JSON.stringify({
            //                 ...values,
            //                 image: imageUrl,
            //             }),
            //         }
            //     );

            //     if (!response.ok) {
            //         const error = await response.json();
            //         throw new Error(error.message || "Failed to create installation");
            //     }

            //     navigate("/agent/categories", {
            //         state: { successMessage: "Installation added successfully!" },
            //     });
            //     showSuccess("Installation added successfully!");
            // } catch (error: any) {
            //     console.error(error);
            //     showError(`Error: ${error.message}`);
            // } finally {
            //     setFormSubmitting(false);
            //     setUploadProgress(0);
            // }
        },
    });

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "removal_requests");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

    const removalSellerOrgId = React.useMemo(
        () => resolveRemovalPaymentSellerOrganizationId(removalOrder, removalOrder?.signage_id),
        [
            removalOrder,
            removalOrder?.signage_id,
            removalOrder?.seller_org_id,
            removalOrder?.seller_owner_node_id,
            removalOrder?.buyer_org_id,
        ]
    );

    useEffect(() => {
        if (!removalOrder || !removalSellerOrgId || !token) return;
        const fetchIntegrations = async () => {
            try {
                const data = await fetchRemovalPaymentGateway(token, removalSellerOrgId);
                setIntegrations(Array.isArray(data) ? data : data ? [data] : []);
            } catch (err) {
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchIntegrations();
    }, [removalOrder, removalSellerOrgId, token]);

    const handleRemovalStatus = async (nextStatus: number): Promise<boolean> => {
        if (!id || !token) return false;
        setLoading(true);

        try {
            let url = "";
            let body: any = undefined;

            // const scheduledDate = removalOrder?.removal_details?.scheduled_date;
            const newDate = formik.values.scheduled_date;
            // const isDateChanged = newDate && newDate !== scheduledDate?.split("T")[0];

            switch (nextStatus) {
                case 200: // Finalize
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/finalize`;
                    break;
                case 600:
                    url = `${process.env.REACT_APP_API_URL}/removal_orders/${id}/cancel`;
                    body = {
                        reason: formik.values.instructions || "Cancelled by user",
                    };
                    break;

                default:
                    showError("Invalid status selected");
                    setLoading(false);
                    return false;
            }

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result?.error || "Status update failed");

            showSuccess(result.message || "Removal status updated");
            setRemovalOrder((prev: any) => ({ ...prev, status: nextStatus }));
            return true;
        } catch (err: any) {
            console.error("Status update error:", err.message);
            showError(err.message || "Something went wrong");
            return false;
        } finally {
            setLoading(false);
        }
    };

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


    useEffect(() => {
        const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
        console.log('Stripe info ', stripeInfo);
        if (stripeInfo && stripeInfo.publishable_key) {
            const promise = loadStripe(stripeInfo.publishable_key);
            setStripePromise(promise);
        }
    }, [integrations]);

    // const completeOrder = async (getmethod: string) => {
    //     console.log("getmethod", getmethod);
    //     setLoading(true);

    //     try {
    //         if (["cheque", "bank_transfer"].includes(getmethod)) {
    //             // 🔁 Call Net30-style API
    //             const latestPayment = removalOrder?.payment_info?.[0]?.amount_due;
    //             console.log("latestPayment", latestPayment);
    //             const lastAmountDue = latestPayment ?? formik.values.total;
    //             console.log("lastAmountDue", lastAmountDue);
    //             const response = await fetch(
    //                 `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/net30_payment`,
    //                 {
    //                     method: "POST",
    //                     headers: {
    //                         "Content-Type": "application/json",
    //                         Authorization: `Bearer ${token}`, // use token from context
    //                     },
    //                     body: JSON.stringify({
    //                         payment_id: formik.values.transactionId || "",
    //                         method: getmethod,
    //                         amount_paid: formik.values.partial_amount,
    //                         total_amount: lastAmountDue,
    //                         amount_due: Math.max(0, lastAmountDue - formik.values.partial_amount),
    //                         payment_date: new Date().toISOString(),
    //                     }),
    //                 }
    //             );
    //             const data = await response.json();
    //             if (!response.ok) {
    //                 throw new Error(data?.error || "Cheque/Bank payment failed.");
    //             }

    //         } else {
    //             // 🔁 Default online payment API
    //             const response = await fetch(
    //                 `${process.env.REACT_APP_API_URL}/removal_orders/${removalOrder?._id}/payment`,
    //                 {
    //                     method: "PUT",
    //                     headers: {
    //                         "Content-Type": "application/json",
    //                         Authorization: `Bearer ${token}`,
    //                     },
    //                     body: JSON.stringify({
    //                         payment_id: "",
    //                         total_amount: formik.values.total,
    //                         amount_paid: 0,
    //                         amount_due: formik.values.total,
    //                         payment_date: new Date().toISOString(),
    //                         method: getmethod,
    //                     }),
    //                 }
    //             );
    //             const data = await response.json();
    //             if (!response.ok) {
    //                 throw new Error(data?.error || "Online payment failed.");
    //             }
    //         }

    //         showSuccess("Payment submitted successfully!");
    //         navigate(`${baseRoute}/jobs/removalRequests`);
    //         localStorage.removeItem("cart_id");

    //     } catch (error) {
    //         const errMessage =
    //             (error as Error).message || "An unknown error occurred";
    //         console.error("Order Failed:", errMessage);
    //         showError(`Error: ${errMessage}`);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const totalPaid = (removalOrder?.payment_info ?? []).reduce(
        (acc: number, payment: { amount_paid?: number | string }) =>
            acc + Number(payment.amount_paid || 0),
        0
    );


    const maxAllowed = (removalOrder?.payment_info ?? []).length > 0 ? Number((Number(removalOrder?.total_amount || 0) - totalPaid).toFixed(2)) : Number(removalOrder?.total_amount || 0);


    const remaingamount = ((removalOrder?.total_amount || 0) - totalPaid)

    const statusHistory: any[] = Array.isArray(removalOrder?.status_history)
        ? (removalOrder?.status_history as any[])
        : [];
    // console.log("statusHistory", statusHistory);
    const paymentInfo: PaymentInfoItem[] = [...(removalOrder?.payment_info || [])];

    const resolveOrderStatus = (status: number | undefined): string => {
        switch (status) {
            case 100: return "Draft";
            case 200: return "Finalized";
            case 300: return "Scheduled";
            case 400: return "In Progress";
            case 500: return "Completed";
            case 600: return "Canceled";
            case 700: return "Losted";
            default: return status !== undefined ? `Status ${status}` : "Unknown";
        }
    };


    const parseDate = (input: any): string => {
        try {
            return new Date(input).toLocaleString();
        } catch {
            return "Invalid Date";
        }
    };

    useEffect(() => {
        const fetchPaymentIntent = async () => {
            // const amountToPay = (formik.values.partial_amount === 0
            //   ? (Number(order?.total_amount || 0) - totalPaid)
            //   : formik.values.partial_amount);

            console.log("amountToPay", (formik.values.partial_amount === 0
                ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                : formik.values.partial_amount));

            try {
                if (!removalSellerOrgId || !token) return;
                const data = await createRemovalStripePaymentIntent(token, removalSellerOrgId, {
                    amount: Math.round(
                        Number(
                            formik.values.partial_amount === 0
                                ? Number(remaingamount || 0)
                                : formik.values.partial_amount
                        ) * 100
                    ),
                    currency: "usd",
                    order_id: String(removalOrder?._id || removalOrder?.id || ""),
                    order_number: String(removalOrder?.order_number || ""),
                    customer_name: String(removalOrder?.user_name || ""),
                    customer_email: String(removalOrder?.user_email || ""),
                    customer_phone: String(removalOrder?.user_phone || ""),
                    payment_method_types: ["card"],
                });
                setClientSecret(data.clientSecret || data.client_secret);
            } catch (err) {
                console.error("Failed to create PaymentIntent:", err);
            }
        };

        if (stripePromise && removalSellerOrgId && removalOrder) fetchPaymentIntent();
    }, [
        stripePromise,
        removalSellerOrgId,
        removalOrder,
        token,
        formik.values.partial_amount,
        removalOrder?.total_amount,
        removalOrder?._id,
        removalOrder?.id,
        removalOrder?.order_number,
        removalOrder?.user_name,
        removalOrder?.user_email,
        removalOrder?.user_phone,
        totalPaid,
        remaingamount,
    ]);

    useEffect(() => {
        const fetchPaymentMethods = async () => {
            const membershipId = resolveRemovalPaymentMethodsMembershipId(removalOrder);
            if (!membershipId || !removalSellerOrgId || !token) {
                return;
            }
            try {
                const data = await fetchRemovalPaymentMethods(token, removalSellerOrgId, membershipId);
                console.log("PaymentMethod :", data);
                setPaymentMethod(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching payment methods:", err);
            } finally {
                setLoading(false);
            }
        };

        if (removalOrder && removalSellerOrgId) {
            fetchPaymentMethods();
        }
    }, [removalOrder, removalSellerOrgId, token]);

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div className="content">
            <div className="page-container">
                <h3 className="m-0 text-center fw-bold addheaders">View Removal Request</h3>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between gap-2 align-items-center pb-3 page-container">
                        <h4 className="table-headers">Order Status : <RemovalStatusBadge value={removalOrder?.status ?? ""} type="order" /></h4>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <h4 className="mt-2"> <RemovalStatusBadge value={removalOrder?.payment_status ?? ""} type="payment" /></h4>
                            <div className="dropdown">
                                <button
                                    className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells"
                                    type="button"
                                    id="orderActionsDropdown"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    Actions
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
                                    {(removalOrder?.status <= 300) && (
                                        <button className="dropdown-item text-danger table-cells" onClick={() => handleRemovalStatus(600)}>
                                            <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Order
                                        </button>
                                    )}
                                    {
                                        removalOrder?.status == 100 && (
                                            <Link
                                                to={`${baseRoute}/jobs/editRemovalRequest/${id}`}
                                                className="dropdown-item text-primary table-cells"
                                            >
                                                <i className="ri-pencil-fill me-2"></i>Edit
                                            </Link>
                                        )
                                    }
                                </ul>
                            </div>
                            <div>
                            </div>
                        </div>
                    </div>
                    <div className="dashed-line my-2"></div>
                    <div className="row mb-3">
                        {/* <div className="col-md-12 d-flex justify-content-between">
                            <div>
                                <div>
                                    <h4>Removal Request Status : {
                                        removalOrder?.status === 100
                                            ? "Draft"
                                            : removalOrder?.status === 200
                                                ? "Finalized"
                                                : removalOrder?.status === 300
                                                    ? "Scheduled"
                                                    : removalOrder?.status === 400
                                                        ? "In Progress"
                                                        : removalOrder?.status === 500
                                                            ? "Completed"
                                                            : removalOrder?.status === 600
                                                                ? "Canceled"
                                                                : removalOrder?.status === 700
                                                                    ? "Losted"
                                                                    : "Unknown"
                                    }</h4>
                                </div>
                            </div>
                        </div> */}
                        <div className="col-md-2 mb-3">
                            <label className="form-label table-cells">
                                Installation Job Order
                            </label>
                            <input type="text" name="installation_order_number" id="installation_order_number" className="form-control" value={`${removalOrder?.
                                installation_order_number || "—"}`} disabled />
                        </div>
                        <div className="col-md-4 mb-3">
                            <label className="form-label table-cells">
                                Installer
                            </label>
                            <input type="text" name="installer" id="installer" className="form-control" value={`${removalOrder?.signage_company_name || "—"}`} disabled />
                        </div>
                        <div className="col-md-2 mb-3">
                            <label className="form-label table-cells">
                                Removal Job Order
                            </label>
                            <input type="text" name="removal_order_number" id="removal_order_number" className="form-control" value={`${removalOrder?.order_number || "—"}`} disabled />
                        </div>

                        <div className="col-md-2 mb-3">
                            <label className="form-label table-cells">
                                Removal Priority
                            </label>
                            <input type="text" name="priority" id="priority" className="form-control" value={`${priorityOptions.find((option) => option.value === removalOrder?.priority)?.label || "—"}`} disabled />
                        </div>

                        <div className="col-md-2 mb-3">
                            <label className="form-label table-cells">
                                Removal Date
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                id="removal_date"
                                name="removal_date"
                                value={formik.values.scheduled_date}
                                disabled
                            />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label table-cells">
                                Assigned To:
                            </label>
                            <input type="text" name="assigned_to" id="assigned_to" className="form-control" value={`${removalOrder?.removal_details?.installer_name || "—"}`} disabled />
                        </div>
                    </div>
                    <hr />
                    <div className="row mb-3">
                        <div className="col-md-12">
                            <h3 className="table-cells">Address details</h3>
                        </div>
                        <div className="col-sm-6">
                            <label
                                htmlFor="billingAddressId"
                                className="form-label table-cells"
                            >
                                Billing Address
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="billingAddress"
                                name="billingAddress"
                                value={`${removalOrder?.billing_address?.addressLine1}, ${removalOrder?.billing_address?.city},  ${removalOrder?.billing_address?.state}`}
                                disabled
                            />
                        </div>
                        <div className="col-sm-6">
                            <label
                                htmlFor="removalAddressId"
                                className="form-label table-cells"
                            >
                                Removal Address
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                id="removalAddressId"
                                name="removalAddressId"
                                value={`${removalOrder?.removal_details?.removal_address?.addressLine1}, ${removalOrder?.removal_details?.removal_address?.city}, ${removalOrder?.removal_details?.removal_address?.state}`}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="dashed-line my-2 "></div>
                    <div className="row mb-3">
                        <div className="col-md-12">
                            <h3 className="mb-3 text-center table-cells">Map View</h3>
                            <div style={mapContainerStyle}>
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    zoom={10}
                                    center={selectedLocation || center}
                                // onClick={handleMapClick}
                                >
                                    {selectedLocation && <Marker position={selectedLocation} />}
                                </GoogleMap>
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-md-12">
                            <h4 className="invoice-subheaders text-center">Product details </h4>

                            <div className="row mb-5 mt-3">
                                <div className="col-md-12 mt-3">
                                    <table className="table table-striped ">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="text-center table-headers" style={{ width: "20%" }}>Product Name</th>
                                                <th className="text-center table-headers" style={{ width: "8%" }}>SKU</th>
                                                <th className="text-center table-headers" style={{ width: "8%" }}> Qty </th>
                                                <th className="text-center table-headers" style={{ width: "12%" }}>Qty to remove </th>
                                                <th className="text-center table-headers" style={{ width: "15%" }}>Product Location Status</th>
                                                <th className="text-center table-headers" style={{ width: "9%" }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational")?.map((item: any, index: number) => (
                                                <tr key={`normal-${item.product_id}`}>
                                                    <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                    <td className="text-center table-cells">{item.sku}</td>
                                                    <td className="text-center table-cells">{item.available_quantity}</td>
                                                    <td className="text-center table-cells">{item.quantity_to_be_removed}</td>
                                                    <td className="text-center table-cells">
                                                        <StatusBadge status={item.current_status?.replace(/_/g, " ") || "--"} />
                                                    </td>
                                                    <td className="text-center table-cells"></td>
                                                    {/* <td></td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                            </div>

                        </div>
                    </div>
                    <div className="row mb-4 align-items-start">
                        {/* Instructions */}
                        <div className="col-md-6">
                            <label className="table-headers mb-2">Instructions</label>
                            <textarea
                                name="instructions"
                                id="instructions"
                                className="form-control"
                                rows={4}
                                value={removalOrder?.removal_details.removal_notes?.map((note: any) => note.value).join("\n") || ""}
                                disabled
                            ></textarea>
                        </div>

                        {/* Gate Code */}
                        <div className="col-md-2">
                            <label className="table-headers mb-2">Gate Code</label>
                            <input
                                type="text"
                                name="gate_code"
                                className="form-control"
                                placeholder="Enter gate code"
                            //   value={formik.values.gate_code}
                            //   onChange={formik.handleChange}
                            //   onBlur={formik.handleBlur}
                            //   disabled={installationOrder?.status > 100}
                            />
                        </div>
                        <div className="col-md-1"></div>
                        {/* Summary Table */}
                        <div className="col-md-3 mt-4 mt-md-0 text-end">
                            <table className="table table-sm text-center align-middle mb-0 text-end">
                                <tbody>
                                    <tr>
                                        <td className="table-headers"><strong>Removal cost:</strong></td>
                                        <td className="table-cells">${Number(formik.values.installation_charge).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="table-headers"><strong>Subtotal:</strong></td>
                                        <td className="table-cells">${Number(formik.values.subtotal).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="table-headers"><strong>Tax:</strong></td>
                                        <td className="table-cells">${Number(formik.values.tax).toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td className="table-headers"><strong>Total:</strong></td>
                                        <td className="table-cells fw-bold">${Number(formik.values.total).toFixed(2)}</td>
                                    </tr>
                                    {(removalOrder?.payment_info ?? []).length > 0 && (
                                        <>
                                            <tr>
                                                <th className="table-headers">Already Paid:</th>
                                                <th className="table-cells">${totalPaid.toFixed(2)}</th>
                                            </tr>
                                            <tr>
                                                <th className="table-headers">Remaining Balance:</th>
                                                <th className="">
                                                    <strong className="table-cells">${remaingamount.toFixed(2)}</strong>
                                                </th>
                                            </tr>
                                        </>
                                    )}

                                    {formik.values.enable_partial_payment && (
                                        <tr>
                                            <th className="table-headers">Partial Payment:</th>
                                            <th className="">
                                                <strong className="table-cells">${Number(formik.values.partial_amount || 0).toFixed(2)}</strong>
                                            </th>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="dashed-line my-2"></div>
                    <div className="row mt-3">
                        <div className="col-md-9">
                            {removalOrder?.payment_status !== "Paid" && removalOrder?.status < 600 && (
                                <>
                                    <h3 className="table-headers">Payment Methods</h3>
                                    <button
                                        type="button"
                                        className="btn btn-success material-shadow-none btn-sm mt-2 mt-sm-0 me-1 mb-3"
                                        onClick={() => setPayment((prev) => !prev)}
                                    >
                                        <i className="mdi mdi-cash-multiple align-middle me-1"></i>
                                        Process Payment
                                    </button>
                                </>
                            )}
                            {payment && removalOrder?._id && (
                                <div className="card-body">
                                    {/* <div className="row my-3">
                                        <div className="col-md-6">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="enable_partial_payment"
                                                    name="enable_partial_payment"
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                        if (!e.target.checked) {
                                                            formik.setFieldValue("partial_amount", 0);
                                                        }
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                    checked={formik.values.enable_partial_payment}
                                                />
                                                <label
                                                    className="form-check-label table-headers"
                                                    htmlFor="enable_partial_payment"
                                                >
                                                    Enable Partial Payment
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            {formik.values.enable_partial_payment && (
                                                <>
                                                    <div className="mt-2">
                                                        <label className="form-label">
                                                            Amount to Collect Now :
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="partial_amount"
                                                            id="partial_amount"
                                                            onChange={(e) => {
                                                                const enteredValue = Number(e.target.value);
                                                                formik.setFieldValue(
                                                                    "partial_amount",
                                                                    enteredValue <= maxAllowed ? enteredValue : maxAllowed
                                                                );
                                                            }}
                                                            onBlur={formik.handleBlur}
                                                            value={formik.values.partial_amount === 0 ? "" : formik.values.partial_amount}
                                                            placeholder="0"
                                                            min={0}
                                                        />

                                                    </div>
                                                    <div className="mt-2">
                                                        <span>
                                                            Remaining Balance: $
                                                            {(Number(remaingamount) - Number(formik.values.partial_amount || 0)).toFixed(2)}

                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div> */}
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            {stripePromise && clientSecret && (
                                                <Elements stripe={stripePromise} options={{ clientSecret }}>
                                                    <div>
                                                        <h5 className="mb-1 table-headers">Payment Selection</h5>
                                                        <p className="text-muted mb-2 table-hedaers">
                                                            Please select payment method
                                                        </p>
                                                    </div>

                                                    {/* Payment Methods */}
                                                    <div className="row">
                                                        <div className="col-lg-3 mb-3 col-sm-6">
                                                            <div className="form-check card-radio">
                                                                <input
                                                                    id="paymentMethodOnline"
                                                                    name="paymentMethod"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    checked={selectedPaymentMethod === "online"}
                                                                    onChange={() =>
                                                                        setSelectedPaymentMethod("online")
                                                                    }
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="paymentMethodOnline"
                                                                >
                                                                    <i className="ri-bank-card-fill align-bottom"></i>
                                                                    <span className="table-cells"> Online Payment</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        {/* <div className="col-lg-3 mb-3 col-sm-6">
                                                            <div className="form-check card-radio">
                                                                <input
                                                                    id="paymentMethodcheque"
                                                                    name="paymentMethod"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    checked={selectedPaymentMethod === "cheque"}
                                                                    onChange={() =>
                                                                        setSelectedPaymentMethod("cheque")
                                                                    }
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="paymentMethodcheque"
                                                                >
                                                                    <i className="ri-bank-card-fill align-bottom"></i>
                                                                    <span className="table-cells"> Cheque</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-lg-3 mb-3 col-sm-6">
                                                            <div className="form-check card-radio">
                                                                <input
                                                                    id="paymentMethodbank_transfer"
                                                                    name="paymentMethod"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    checked={selectedPaymentMethod === "bank_transfer"}
                                                                    onChange={() =>
                                                                        setSelectedPaymentMethod("bank_transfer")
                                                                    }
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="paymentMethodbank_transfer"
                                                                >
                                                                    <i className="ri-bank-card-fill align-bottom"></i>
                                                                    <span className="table-cells"> Bank Transfer</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                        {(formik.values.enable_partial_payment || removalOrder?.payment_info?.length) ? <></> :
                                                            (
                                                                paymentMethod?.map((method) => (
                                                                    <div className="col-lg-3 mb-3 col-sm-6" key={method.id}>
                                                                        <div className="form-check card-radio">
                                                                            <input
                                                                                id={`paymentMethod_${method.id}`}
                                                                                name="paymentMethod"
                                                                                type="radio"
                                                                                className="form-check-input"
                                                                                checked={
                                                                                    selectedPaymentMethod === method.name
                                                                                }
                                                                                onChange={() =>
                                                                                    setSelectedPaymentMethod(method.name)
                                                                                }
                                                                            />
                                                                            <label
                                                                                className="form-check-label"
                                                                                htmlFor={`paymentMethod_${method.id}`}
                                                                            >
                                                                                <i className="ri-bank-card-fill align-bottom"></i>
                                                                                <span> {method.name}</span>
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )
                                                        } */}
                                                    </div>

                                                    {selectedPaymentMethod === "online" ? (
                                                        <div className="collapse show mt-4">
                                                            <div className="">
                                                                <PaymentFormManualOrder
                                                                    url="removal_orders"
                                                                    method="POST"
                                                                    supplierId={removalSellerOrgId}
                                                                    totalAmount={remaingamount ? remaingamount : removalOrder?.total_amount}
                                                                    paidAmount={
                                                                        formik.values.partial_amount === 0
                                                                            ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                                            : formik.values.partial_amount
                                                                    }
                                                                    orderId={removalOrder?._id}
                                                                    selectedPaymentMethod={selectedPaymentMethod}
                                                                    onPaymentSuccess={(paymentIntentId) => {
                                                                        console.log(
                                                                            "Payment Success: ",
                                                                            paymentIntentId
                                                                        );
                                                                        // formik.handleSubmit(); // Proceed to order completion
                                                                    }}
                                                                    userId={
                                                                        resolveRemovalPaymentMethodsMembershipId(removalOrder) || ""
                                                                    }
                                                                    userEmail={removalOrder?.user_email}
                                                                    paidBy_id={userId?.toString() ?? ""}
                                                                    paidBy_name={userName ?? ""}
                                                                    payments_status={removalOrder?.payment_status ?? ""}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        // ["cheque", "bank_transfer"].includes(selectedPaymentMethod) ? (
                                                        //     <div className="mt-4">
                                                        //         <div className="card p-3 border shadow-none">
                                                        //             <h4 className="mb-3">
                                                        //                 {selectedPaymentMethod === "cheque" ? "Cheque Payment Details" : "Bank Transfer Details"}
                                                        //             </h4>
                                                        //             <div className="row">
                                                        //                 <div className="mb-3 col-md-6">
                                                        //                     <label htmlFor="transactionId" className="form-label">
                                                        //                         Transaction ID
                                                        //                     </label>
                                                        //                     <input
                                                        //                         type="text"
                                                        //                         className="form-control"
                                                        //                         id="transactionId"
                                                        //                         value={formik.values.transactionId} // Adjust as needed or manage with new state
                                                        //                         onChange={(e) => formik.setFieldValue("transactionId", e.target.value)}
                                                        //                     />
                                                        //                 </div>

                                                        //                 <div className="mb-3 col-md-6">
                                                        //                     <label htmlFor="amount" className="form-label">
                                                        //                         Amount
                                                        //                     </label>
                                                        //                     <input
                                                        //                         type="number"
                                                        //                         className="form-control"
                                                        //                         id="amount"
                                                        //                         value={formik.values.partial_amount === 0
                                                        //                             ? (Number(removalOrder?.total_amount || 0) - totalPaid)
                                                        //                             : formik.values.partial_amount}
                                                        //                         // onChange={(e) =>
                                                        //                         //     formik.setFieldValue(
                                                        //                         //         "partial_amount",
                                                        //                         //         Number(e.target.value) <= maxAllowed ? Number(e.target.value) : maxAllowed
                                                        //                         //     )
                                                        //                         // }
                                                        //                         disabled
                                                        //                     />
                                                        //                 </div>
                                                        //             </div>

                                                        //             <button
                                                        //                 type="button"
                                                        //                 className="btn btn-primary"
                                                        //                 onClick={() => {
                                                        //                     completeOrder(selectedPaymentMethod);
                                                        //                 }}
                                                        //             >
                                                        //                 Submit Payment
                                                        //             </button>
                                                        //         </div>
                                                        //     </div>
                                                        // ) : (
                                                        //     selectedPaymentMethod ? (
                                                        //         <div className="d-flex align-items-start gap-3 mt-4">
                                                        //             <button
                                                        //                 type="button"
                                                        //                 className="btn btn-primary"
                                                        //                 onClick={() => {
                                                        //                     completeOrder(selectedPaymentMethod);
                                                        //                 }}
                                                        //             >
                                                        //                 Complete Order
                                                        //             </button>
                                                        //         </div>
                                                        //     ) : null
                                                        // )
                                                        <></>
                                                    )
                                                    }
                                                </Elements>
                                            )}
                                            {(() => {
                                                const membershipId = resolveRemovalPaymentMethodsMembershipId(removalOrder);
                                                return (
                                                    <PaymentMethods orderId={removalOrder?._id || ""} totalAmount={remaingamount ? remaingamount : removalOrder?.total_amount || 0} supplierId={removalSellerOrgId} id={membershipId} ordertype="removalOrder"
                                                    />
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0 table-headers">Installation Log</h5>
                        </div>
                        <div className="card-body">
                            <ul className="list-unstyled vstack gap-3">
                                {
                                    <strong className="table-cells">Order Created on {parseDate(removalOrder?.created_at)}</strong>
                                }
                                {/* ✅ Payment Logs */}
                                {paymentInfo.slice().reverse()?.map((payment, idx) => {
                                    const status =
                                        payment.amount_due === 0
                                            ? "Paid"
                                            : payment.amount_paid > 0
                                                ? "Partial Payment"
                                                : "Pending";

                                    const badgeClass =
                                        status === "Paid"
                                            ? "bg-success-subtle text-success"
                                            : status === "Partial Payment"
                                                ? "bg-warning-subtle text-warning"
                                                : "bg-danger-subtle text-danger";

                                    return (
                                        <li key={`payment-${idx}`}>
                                            <div>
                                                <strong className="table-cells">💰 Payment:</strong>{" "}
                                                <span className="badge bg-success-subtle text-success">
                                                    ${payment.amount_paid?.toFixed(2)} {payment.method && `via ${payment.method}`}
                                                </span>

                                                <strong className="table-cells ms-3">💳 Payment Status:</strong>{" "}
                                                <span className={`badge ${badgeClass}`}>{status}</span>

                                                {/* ✅ Paid By Name */}
                                                <span className="ms-3 table-cells">
                                                        <i className="ri-user-line align-middle me-1"></i>
                                                        Paid by: <strong>{displayPaymentPaidByName(payment)}</strong>
                                                    </span>

                                                <div className="text-muted small">On {parseDate(payment.payment_date)}</div>
                                            </div>
                                        </li>
                                    );
                                })}

                                {/* ✅ Installation Status Logs */}
                                {statusHistory?.map((log, idx) => (
                                    <li key={`status-${idx}`}>
                                        <div>
                                            <strong className="table-cells">📦 Installation Status:</strong>{" "}
                                            <span className="badge bg-secondary-subtle text-dark table-cells">
                                                {resolveOrderStatus(log.from)} → {resolveOrderStatus(log.to)}
                                            </span>
                                            <div className="text-muted small table-cells">On {parseDate(log.timestamp)}</div>
                                            {log.note && <div className="small mt-1 table-cells">📝 {log.reason}</div>}
                                        </div>
                                    </li>
                                ))}

                            </ul>
                        </div>
                    </div>
                    <div className="dashed-line my-2"></div>
                    <div className="row mb-3 rcust-margin-4">
                        <div className="col-md-12 d-flex justify-content-between align-items-center">
                            <button
                                className="btn btn-outline-secondary br-8 backbutton"
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>

                            <div>
                                <div className="text-end">
                                    <strong className="table-headers">Local Laws Compliance:</strong> {removalOrder?.local_laws_compliance ? "Acknowledged" : "Not Acknowledged"}
                                </div>
                                <div className="row mb-3 text-end">
                                    <div className="col-md-12">
                                        <Link to={`${baseRoute}/jobs/removalRequests`} className="btn btn-info">Back to Orders</Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </div >
    );
};

export default RealtorViewRemovalRequest;
